import { useRef } from 'react';
import { Button } from '../../ui';
import styles from '../Clustering.module.css';

interface KeywordsInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Free-form keyword editor: newline-separated textarea plus a CSV/TXT file
 * picker that appends parsed keywords. Server will dedupe + normalize.
 */
export function KeywordsInput({ value, onChange, disabled }: KeywordsInputProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const count = value
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean).length;

  const handleFile = async (file: File) => {
    const text = await file.text();
    const rows = text
      .split(/\r?\n/)
      .map((line) => line.split(',')[0]?.trim() ?? '')
      .filter((kw) => kw && kw.toLowerCase() !== 'keyword');
    const merged = value ? `${value.replace(/\s+$/, '')}\n${rows.join('\n')}` : rows.join('\n');
    onChange(merged);
  };

  return (
    <div className={styles.formRow}>
      <label className={styles.formLabel} htmlFor="clustering-keywords">
        Keywords ({count})
      </label>
      <textarea
        id="clustering-keywords"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="One keyword per line, e.g.:&#10;best running shoes&#10;running shoes for flat feet&#10;trail running shoes review"
        disabled={disabled}
        rows={12}
        style={{
          width: '100%',
          padding: 'var(--spacing-3)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          fontFamily: 'var(--font-family-mono, monospace)',
          fontSize: 'var(--font-size-sm)',
          background: 'var(--color-background-primary)',
          color: 'var(--color-text-primary)',
          resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.txt"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = '';
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
        >
          Upload CSV / TXT
        </Button>
        {count > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange('')}
            disabled={disabled}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
